<template>
  <v-menu
    ref="dialog"
    v-model="dialog"
    absolute
    :close-on-content-click="false"
    :content-class="paraNoteMenuClass"
  >
    <v-card class="para-notes">
      <v-textarea
        v-model="currentItem.note"
        rows="3"
        :placeholder="$t('Context.addParagraphNote')"
      />
      <div class="block-buttons">
        <button
          :class="{ active: isTypeBeforePara }"
          @click="selectTypeBeforePara()"
        >
          <BaseSpriteIcon icon-name="ico-check" />
          {{ $t('ContextNote.beforeParagraph.label') }}
        </button>
        <button
          :class="{ active: isTypeAfterPara }"
          @click="selectTypeAfterPara()"
        >
          <BaseSpriteIcon icon-name="ico-check" />
          {{ $t('ContextNote.afterParagraph.label') }}
        </button>
      </div>
      <div v-if="!isConfirmDeletionMode" class="footer-block">
        <div class="button-wrap" @click="saveItem()">
          <BaseSpriteIcon icon-name="ico-check" />
        </div>

        <div v-if="isInEditingMode" class="button-wrap" @click="deleteItem()">
          <BaseSpriteIcon icon-name="ico-delete" />
        </div>
        <div class="button-wrap" @click="cancel()">
          <BaseSpriteIcon icon-name="ico-close" />
        </div>
      </div>
      <div v-if="isConfirmDeletionMode" class="footer-block">
        <div class="button-wrap" @click="confirmDeletion()">
          {{ $t('Delete') }}
        </div>
        <div class="button-wrap" @click="cancelDeletion()">
          {{ $t('Cancel') }}
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('ParaNoteContextMenu.vue');

import MaterialsFactory from '@/classes/factories/Materials/MaterialsFactory';
import ParaNoteContextMenuEvents from '@/enums/ParaNoteContextMenuEvents';
import PopupEventEnum from '@/enums/PopupEventEnum';
import MaterialTypes from '@shared/enums/MaterialTypes';
import MaterialPositions from '@/enums/MaterialPositions';
import MenuStates from '@/enums/MenuStates';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';

import cloneDeep from 'lodash/cloneDeep';

const PARA_NOTE_MENU_CLASS = 'para-context-menu';

export default {
  name: 'ParaNoteContextMenu',
  components: { BaseSpriteIcon },
  props: {
    paraNoteMenuParams: { type: Object, required: true },
    menuMode: String,
    locator: String,
    paragraphNote: Object
  },
  data() {
    const paraNoteMenuClass = PARA_NOTE_MENU_CLASS;
    const paraNoteBuilder = MaterialsFactory.createBuilderByType(
      MaterialTypes.PARA_NOTE
    );
    const defaultCurrentItem = paraNoteBuilder
      .setParagraphId(this.paraNoteMenuParams.paragraphId)
      .build();
    return {
      paraNoteMenuClass,
      defaultCurrentItem,
      currentItem: cloneDeep(defaultCurrentItem),
      isConfirmDeletionMode: false,
      dialog: null
    };
  },
  computed: {
    isTypeBeforePara() {
      return this.currentItem.position === MaterialPositions.BEFORE_PARAGRAPH;
    },
    isTypeAfterPara() {
      return this.currentItem.position === MaterialPositions.AFTER_PARAGRAPH;
    },
    isInEditingMode() {
      return this.paraNoteMenuParams.menuMode === MenuStates.EDIT;
    },
    isInAddingMode() {
      return this.paraNoteMenuParams.menuMode === MenuStates.ADD;
    }
  },
  watch: {
    menuMode(newMode) {
      if (newMode === MenuStates.ADD) {
        this.currentItem = { ...this.defaultCurrentItem };
        return;
      }
      this.currentItem = { ...this.paragraphNote };
    },
    dialog(val) {
      if (!val) {
        this.$emit('paraNoteContextMenuEvent', {
          type: PopupEventEnum.CLOSE
        });
      }
    }
  },
  mounted() {
    this.dialog = true;
    if (this.isInEditingMode) {
      const paraNote = this.paraNoteMenuParams.paragraphNote;
      this.currentItem
        .changeNote(paraNote.note)
        .changePosition(paraNote.position)
        .changeId(paraNote.id);
    }

    setTimeout(() => {
      try {
        this.$_setMenuPosition();
      } catch (err) {
        this.$_paraNoteMenuErrorHandler(err);
      }
    }, 100);
  },
  methods: {
    saveItem() {
      if (!this.currentItem.note) {
        this.$_closePopup();
        return;
      }
      switch (this.paraNoteMenuParams.menuMode) {
        case MenuStates.ADD:
          try {
            this.$store.dispatch('MaterialsStore/addParaNote', {
              reqParams: this.paraNoteMenuParams.reqParams,
              paraNote: this.currentItem
            });
          } catch (err) {
            this.$_paraNoteMenuErrorHandler(err);
          }

          this.$_closePopup();
          break;

        case MenuStates.EDIT:
          this.$store.dispatch('MaterialsStore/changeParaNote', {
            reqParams: this.paraNoteMenuParams.reqParams,
            editedParaNote: this.currentItem
          });
          this.$_closePopup();
          break;
      }
    },
    $_setMenuPosition() {
      const contextMenu = window.document.querySelector(
        `.${this.paraNoteMenuClass}`
      );
      for (let key in this.paraNoteMenuParams.menuPosition) {
        contextMenu.style[key] = this.paraNoteMenuParams.menuPosition[key];
      }
    },
    deleteItem() {
      this.isConfirmDeletionMode = true;
    },
    confirmDeletion() {
      this.$store.dispatch('MaterialsStore/deleteParaNote', {
        reqParams: this.paraNoteMenuParams.reqParams,
        paraNoteToDelete: this.currentItem
      });
      this.$_closePopup();
    },
    cancelDeletion() {
      this.isConfirmDeletionMode = false;
    },
    cancel() {
      this.$_closePopup();
    },
    selectTypeBeforePara() {
      this.currentItem.position = MaterialPositions.BEFORE_PARAGRAPH;
    },
    selectTypeAfterPara() {
      this.currentItem.position = MaterialPositions.AFTER_PARAGRAPH;
    },
    $_paraNoteMenuErrorHandler(error) {
      logger.error(`Error in ParaNoteContextMenu: ${error}`);
      this.$emit('paraNoteContextMenuEvent', {
        type: ParaNoteContextMenuEvents.ERROR,
        data: { error }
      });
    },
    $_closePopup() {
      this.dialog = false;
    },
    $_getPopupElement() {
      return this.$refs.contextDialog.$el.querySelector('.v-dialog');
    }
  }
};
</script>

<style lang="less" src="./ParaNoteContextMenu.less"></style>
